import type {SortableEvent} from 'sortablejs';
import type {TicketingConfig, TicketingDonationConfig, TicketingQuestion} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {
  getDefaultTicketingConfig,
  getDefaultTicketingDonationConfig,
  MAX_DONATION_DESCRIPTION_LENGTH,
  getDefaultTicketingFormTitle,
  MAX_PER_ORDER,
  MAX_PRICE,
  MAX_TOTAL_TICKETS,
  MIN_CHOICE_OPTIONS,
  MIN_DONATION_AMOUNT,
  MIN_PRICE,
  QuestionBelongsTo,
  QuestionInputType,
  TaxMode,
} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import type {RegistrationModeState} from '@Components/event-wizard/event-wizard.types';
import {RegistrationMode} from '@Components/event-wizard/event-wizard.types';
import type {EventRegistrationFields} from '@Components/events/events.types';
import {getButtonTextOptionsForMode} from '@Libraries/events.library';
import {GA4EventParamName} from '@Libraries/ga-events';
import type {TicketingEventTypeParam} from '@Libraries/ticketing-ga4.types';
import {
  TicketingFeeStrategyParam,
  TicketingFormScopeParam,
  TicketingPaidFreeParam,
  TicketingRegistrationTypeParam,
  TicketingTaxParam,
} from '@Libraries/ticketing-ga4.types';
import {isValidURL2} from '@Utils/url.util';
import {assertNever} from '@Utils/assert-never.util';

/**
 * Undoes the DOM node move sortablejs performs on drop, restoring the dragged element to its
 * original position so React stays the single source of truth (we re-render from state after
 * dispatching the canonical reorder). Accounts for the post-move index shift: for a downward drag
 * the node that originally followed the dragged item now sits at `oldIndex`; for an upward drag the
 * dragged item shifted everything forward, so that following node sits at `oldIndex + 1`. A `null`
 * reference appends (move-to-end). Returns `false` (no-op) when the drop did not change position.
 */
export const revertSortableDomMove = (event: SortableEvent): boolean => {
  const {oldIndex, newIndex, item, from} = event;
  if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) {
    return false;
  }

  const referenceNode = (newIndex < oldIndex ? from.children[oldIndex + 1] : from.children[oldIndex]) ?? null;
  from.insertBefore(item, referenceNode);
  return true;
};

/**
 * Maps the inbound (camelCase) ticketing config carried on `eventVO.ticketingConfig` into a
 * well-formed Redux `TicketingConfig`. Returns the default config when `raw` is absent.
 *
 * Missing optional fields fall back to the defaults; each question is mapped in place so array
 * position stays the single source of truth for ordering (the persisted per-question `order` is
 * intentionally ignored). `registerBtnText` is the single persisted CTA label (the backend no longer
 * stores a separate ticketing `buttonText`), so it seeds only the loaded tile's picker value — Tickets
 * or RSVP per `isRsvp` — leaving the other tile's field empty so it falls back to its own default.
 *
 * An event saved before ticketing existed has no `raw` but still carries a CTA label, and derives to the
 * RSVP tile — so the label must seed `rsvpButtonText` there too, or saving that event overwrites the
 * organiser's chosen label with the RSVP default.
 *
 * `maxPerOrder` is the one field an RSVP config must not hand over: RSVP has no such input, so its stored value is
 * always the "no limit" placeholder the backend presets (`buildRsvpPresetConfig_`), never a choice. In the Tickets
 * form that placeholder reads as a real default and blocks the step as soon as a smaller total is entered.
 */
export const buildTicketingConfigFromEventVO = (raw?: TicketingConfig, registerBtnText = ''): TicketingConfig => {
  const defaults = getDefaultTicketingConfig();

  if (!raw) {
    return {...defaults, rsvpButtonText: registerBtnText};
  }

  const isRsvp = raw.isRsvp ?? defaults.isRsvp;

  return {
    isSelected: raw.isSelected ?? defaults.isSelected,
    isRsvp,
    ticketName: raw.ticketName ?? defaults.ticketName,
    price: raw.price ?? defaults.price,
    totalTickets: raw.totalTickets ?? defaults.totalTickets,
    maxPerOrder: isRsvp ? defaults.maxPerOrder : raw.maxPerOrder ?? defaults.maxPerOrder,
    passFeesToBuyer: raw.passFeesToBuyer ?? defaults.passFeesToBuyer,
    taxLabel: raw.taxLabel ?? defaults.taxLabel,
    taxRatePercent: raw.taxRatePercent ?? defaults.taxRatePercent,
    taxMode: raw.taxMode ?? defaults.taxMode,
    buttonText: isRsvp ? defaults.buttonText : registerBtnText,
    rsvpButtonText: isRsvp ? registerBtnText : defaults.rsvpButtonText,
    whoResponds: raw.whoResponds ?? defaults.whoResponds,
    questionSectionTitle: raw.questionSectionTitle ?? defaults.questionSectionTitle,
    questionSectionDescription: raw.questionSectionDescription ?? defaults.questionSectionDescription,
    questions: (raw.questions ?? defaults.questions).map(
      (question): TicketingQuestion => ({
        text: question.text,
        type: question.type,
        required: question.required,
        options: question.options,
      })
    ),
    donation: buildTicketingDonationConfigFromEventVO(raw.donation),
  };
};

/**
 * The donation half of the hydration. Configs saved before donations existed have no `donation` key, and the
 * server sends a null description/max for unset fields — both read as the defaults.
 */
const buildTicketingDonationConfigFromEventVO = (raw?: Partial<TicketingDonationConfig> | null): TicketingDonationConfig => {
  const defaults = getDefaultTicketingDonationConfig();
  if (!raw) {
    return defaults;
  }

  return {
    enabled: raw.enabled ?? defaults.enabled,
    description: raw.description ?? defaults.description,
    minAmount: raw.minAmount ?? defaults.minAmount,
    maxAmount: raw.maxAmount ?? defaults.maxAmount,
  };
};

/**
 * The session-stored CTA label backing a tile's button-text picker. Each tile keeps its own field so
 * switching tiles preserves per-tile choices: Tickets and RSVP each have one on the ticketing config,
 * Custom Link uses the registration details. Empty ⇒ the organiser hasn't picked a label for that tile,
 * so the picker shows the mode's first preset as the default. The single mode→field mapping, shared by
 * the picker (its value) and the save-time consolidation (`getEffectiveRegisterBtnText`).
 */
export const getStoredButtonTextForMode = (mode: RegistrationMode, ticketingConfig: TicketingConfig, registrationDetails: EventRegistrationFields): string => {
  switch (mode) {
    case RegistrationMode.TICKETS:
      return ticketingConfig.buttonText;
    case RegistrationMode.RSVP:
      return ticketingConfig.rsvpButtonText;
    case RegistrationMode.CUSTOM_LINK:
    case RegistrationMode.NONE:
      return registrationDetails.registerBtnText;
    default:
      return assertNever(mode);
  }
};

/**
 * The CTA label the published event page renders for a mode: the organiser's stored per-mode choice,
 * falling back to that mode's first preset when they left it empty. Shared by the save path (which
 * consolidates it into `register_btn_text`) and the live preview so the preview button matches the
 * published page.
 */
export const getEffectiveRegisterBtnText = (mode: RegistrationMode, ticketingConfig: TicketingConfig, registrationDetails: EventRegistrationFields): string => {
  return getStoredButtonTextForMode(mode, ticketingConfig, registrationDetails) || getButtonTextOptionsForMode(mode)[0];
};

export const requiresOptions = (type: QuestionInputType): boolean => {
  return type === QuestionInputType.SINGLE_SELECT || type === QuestionInputType.MULTIPLE_CHOICE;
};

/** Registration mode → the Events_Ticketing_Type_Switch `to` value (1:1). */
export const REGISTRATION_MODE_TO_GA4_PARAM: Record<RegistrationMode, TicketingRegistrationTypeParam> = {
  [RegistrationMode.TICKETS]: TicketingRegistrationTypeParam.TICKETS,
  [RegistrationMode.RSVP]: TicketingRegistrationTypeParam.RSVP,
  [RegistrationMode.CUSTOM_LINK]: TicketingRegistrationTypeParam.CUSTOM_LINK,
  [RegistrationMode.NONE]: TicketingRegistrationTypeParam.NONE,
};

/** The single price→paid decision for a ticketing config, shared by the GA4 event_type and setup-complete params. */
export const isTicketingConfigPaid = (ticketingConfig: TicketingConfig): boolean => ticketingConfig.price > 0;

/** Events_Create_Complete `event_type`: RSVP/CUSTOM_LINK/NONE pass through; TICKETS resolves to paid/free by price. */
export const getEventTypeGa4Param = (mode: RegistrationMode, ticketingConfig: TicketingConfig): TicketingEventTypeParam => {
  switch (mode) {
    case RegistrationMode.TICKETS:
      return isTicketingConfigPaid(ticketingConfig) ? TicketingPaidFreeParam.PAID : TicketingPaidFreeParam.FREE;
    case RegistrationMode.RSVP:
      return TicketingRegistrationTypeParam.RSVP;
    case RegistrationMode.CUSTOM_LINK:
      return TicketingRegistrationTypeParam.CUSTOM_LINK;
    case RegistrationMode.NONE:
      return TicketingRegistrationTypeParam.NONE;
    default:
      return assertNever(mode);
  }
};

const getTicketingTaxGa4Param = (ticketingConfig: TicketingConfig): TicketingTaxParam => {
  if (ticketingConfig.taxLabel.trim() === '' || ticketingConfig.taxRatePercent <= 0) {
    return TicketingTaxParam.NONE;
  }
  return ticketingConfig.taxMode === TaxMode.INCLUSIVE ? TicketingTaxParam.INCLUSIVE : TicketingTaxParam.EXCLUSIVE;
};

/** The four Events_Ticketing_Setup_Complete params derived from the saved ticketing config. */
export const getTicketingSetupCompleteParams = (ticketingConfig: TicketingConfig): Record<string, string> => {
  return {
    [GA4EventParamName.TYPE]: isTicketingConfigPaid(ticketingConfig) ? TicketingPaidFreeParam.PAID : TicketingPaidFreeParam.FREE,
    [GA4EventParamName.FEE_STRATEGY]: ticketingConfig.passFeesToBuyer ? TicketingFeeStrategyParam.PASS : TicketingFeeStrategyParam.ABSORB,
    [GA4EventParamName.FORM_SCOPE]: ticketingConfig.whoResponds === QuestionBelongsTo.PRODUCT ? TicketingFormScopeParam.ALL_ATTENDEES : TicketingFormScopeParam.BUYER,
    [GA4EventParamName.TAX]: getTicketingTaxGa4Param(ticketingConfig),
  };
};

/**
 * The single "non-empty option" predicate. Used identically by the commit-enable check and the
 * on-commit prune so the commit button never enables while pruning would drop below the
 * MIN_CHOICE_OPTIONS floor.
 */
export const isNonEmptyOption = (option: string): boolean => {
  return option.trim().length > 0;
};

/**
 * Normalizes a draft question's options for persistence: choice types keep only the non-empty
 * options (empties pruned); non-choice types force `[]` so phantom draft options never carry into
 * Redux / the POST when the user set options then switched to a non-choice type.
 */
export const pruneTicketingQuestionOptions = (question: TicketingQuestion): TicketingQuestion => {
  return {
    ...question,
    options: requiresOptions(question.type) ? question.options.filter(isNonEmptyOption) : [],
  };
};

/**
 * True when a draft question is complete enough to commit: trimmed text is non-empty and, for
 * choice types, at least MIN_CHOICE_OPTIONS non-empty options survive pruning. The single validation
 * point for questions — committed questions are valid by construction, so the footer gate is not extended.
 */
export const isTicketingQuestionValid = (question: TicketingQuestion): boolean => {
  if (question.text.trim().length === 0) {
    return false;
  }
  if (requiresOptions(question.type)) {
    return question.options.filter(isNonEmptyOption).length >= MIN_CHOICE_OPTIONS;
  }
  return true;
};

export const isTicketingPriceValid = (price: number): boolean => {
  if (!Number.isFinite(price) || price < 0) {
    return false;
  }
  if (price === 0) {
    return true;
  }
  return price >= MIN_PRICE && price <= MAX_PRICE;
};

export const isTicketingTotalTicketsValid = (total: number | null): boolean => {
  if (total === null) {
    return true;
  }
  return Number.isInteger(total) && total >= 1 && total <= MAX_TOTAL_TICKETS;
};

/**
 * Max per order is a set integer of at least 1 that never exceeds the effective cap. A null value
 * means the field was cleared mid-edit; that is accepted because it defaults to DEFAULT_MAX_PER_ORDER
 * on blur, so an empty field never blocks the step. The cap mirrors the backend clamp
 * `min($maxPerOrder, $totalAvailableTickets ?? MAX_PER_ORDER)` (EventPostRequests.php): the finite
 * total when one is set, otherwise `MAX_PER_ORDER` for unlimited inventory.
 */
export const isTicketingMaxPerOrderValid = (max: number | null, total: number | null): boolean => {
  if (max === null) {
    return true;
  }
  if (!Number.isInteger(max) || max < 1) {
    return false;
  }
  return max <= (total ?? MAX_PER_ORDER);
};

/**
 * Max per order held within the event's own inventory; a null total is unlimited. Mirrors the backend's save-time
 * clamp (EventPostRequests.php) so the wizard never blocks the step over a max per order the organiser did not
 * type — both the default and a value inherited from another mode can exceed a small total.
 */
export const clampMaxPerOrderToTotal = (maxPerOrder: number, totalTickets: number | null): number => {
  return Math.min(maxPerOrder, totalTickets ?? Infinity);
};

/**
 * True when every ticket-form field is valid. Used by the footer gate.
 * Intentionally does NOT check `isSelected` (the footer guards that) nor the questions sub-form
 * (Phase 2b).
 */
export const isTicketingFormValid = (config: TicketingConfig): boolean => {
  return (
    isTicketingPriceValid(config.price) &&
    isTicketingTotalTicketsValid(config.totalTickets) &&
    isTicketingMaxPerOrderValid(config.maxPerOrder, config.totalTickets)
  );
};

export const isTicketingDonationMinAmountValid = (minAmount: number): boolean => {
  return Number.isFinite(minAmount) && minAmount >= MIN_DONATION_AMOUNT && minAmount <= MAX_PRICE;
};

/** A null maximum is unlimited; a set one must sit strictly above the minimum (the save path rejects anything else). */
export const isTicketingDonationMaxAmountValid = (maxAmount: number | null, minAmount: number): boolean => {
  if (maxAmount === null) {
    return true;
  }
  return Number.isFinite(maxAmount) && maxAmount > minAmount && maxAmount <= MAX_PRICE;
};

/**
 * True when the donation add-on is off, or on with savable bounds. Text is clamped server-side, but the lengths are
 * checked here too so the modal's Save never looks successful while the server trims the copy.
 */
export const isTicketingDonationValid = (donation: TicketingDonationConfig): boolean => {
  if (!donation.enabled) {
    return true;
  }
  return (
    donation.description.length <= MAX_DONATION_DESCRIPTION_LENGTH &&
    isTicketingDonationMinAmountValid(donation.minAmount) &&
    isTicketingDonationMaxAmountValid(donation.maxAmount, donation.minAmount)
  );
};

/**
 * Whether the active registration config is PAID ticketing — the case that requires Stripe payout
 * onboarding before the wizard advances. Keeps the price/mode definition of "paid ticketing" in the
 * ticketing module rather than inlined in the wizard footer.
 */
export const isPaidTicketingConfig = (mode: RegistrationMode, ticketingConfig: TicketingConfig): boolean => {
  return mode === RegistrationMode.TICKETS && ticketingConfig.price > 0;
};

/**
 * Whether the active registration config takes money: paid tickets, or RSVP / free tickets with donations on. Mirrors
 * TicketingConfigVO::collectsPayments(); it is what gates the Premium + US upsell before the wizard advances.
 */
export const doesTicketingConfigCollectPayments = (mode: RegistrationMode, ticketingConfig: TicketingConfig): boolean => {
  if (isPaidTicketingConfig(mode, ticketingConfig)) {
    return true;
  }
  return (mode === RegistrationMode.TICKETS || mode === RegistrationMode.RSVP) && ticketingConfig.donation.enabled;
};

/** Whether the pass-fees toggle means anything: there is a ticket price or a donation for the fees to sit on. */
export const shouldShowTicketingPassFeesToggle = (ticketingConfig: TicketingConfig): boolean => {
  return (!ticketingConfig.isRsvp && ticketingConfig.price > 0) || ticketingConfig.donation.enabled;
};

/**
 * The custom-form title to persist/preview: the organiser's value, falling back to the mode's default
 * when they leave it empty. The single source of the empty-title fallback used by the save and preview
 * paths (mirrors `getEffectiveRegisterBtnText`); the field itself shows the default as a placeholder.
 */
export const getEffectiveQuestionSectionTitle = (config: TicketingConfig): string => {
  return config.questionSectionTitle || getDefaultTicketingFormTitle(config);
};

/**
 * Custom Link mode requires a valid button URL. Uses the same `isValidURL2` predicate the save path
 * (`getRegistrationFieldsData`) uses to decide whether to send `register_btn_link`, so the gate and
 * the wire agree on what counts as a link. An empty/undefined link is invalid.
 */
export const isCustomLinkRegistrationValid = (registrationDetails: EventRegistrationFields): boolean => {
  return isValidURL2(registrationDetails.registerBtnLink ?? '');
};

/**
 * The single step-2 (Tickets & registration) completion gate. Validates ONLY the active mode: the
 * save path omits every inactive mode's data, so validating all modes at once would wrongly block a
 * valid event (e.g. a stale ticket price while the user is in RSVP).
 */
export const isStep2Complete = (
  mode: RegistrationMode,
  ticketingConfig: TicketingConfig,
  registrationDetails: EventRegistrationFields
): boolean => {
  switch (mode) {
    case RegistrationMode.TICKETS:
      return isTicketingFormValid(ticketingConfig) && isTicketingDonationValid(ticketingConfig.donation);
    case RegistrationMode.CUSTOM_LINK:
      return isCustomLinkRegistrationValid(registrationDetails);
    case RegistrationMode.RSVP:
      return isTicketingDonationValid(ticketingConfig.donation);
    default:
      return true;
  }
};

/**
 * The single registration-mode → wizard-state mapping.
 *
 * Recurring RSVP is fully supported — provisioned lazily on first registration rather than at save — so
 * recurrence changes exactly one answer: Tickets cannot exist on a recurring event, and converts to RSVP here
 * rather than in the callers, so no code path can leave the wizard showing a selected-but-impossible Tickets
 * tile. RSVP rather than None because an organiser who set up tickets still wants registrations; what they lose
 * is only the paid half. The inline alert in the Repeat panel and the disabled Tickets tile are what say so.
 */
export const resolveRegistrationModeState = (mode: RegistrationMode, isRecurring: boolean): RegistrationModeState => {
  const resolvedMode = isRecurring && mode === RegistrationMode.TICKETS ? RegistrationMode.RSVP : mode;

  return {
    mode: resolvedMode,
    isSelected: resolvedMode === RegistrationMode.TICKETS || resolvedMode === RegistrationMode.RSVP,
    isRsvp: resolvedMode === RegistrationMode.RSVP,
    areRegistrationsRequired: resolvedMode === RegistrationMode.CUSTOM_LINK,
  };
};
